import { useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { SubscriptionInterval, SubscriptionPlanTarget, SubscriptionTier } from '~/apps/subscriptions/types';
import { getSessionStorageValue, setSessionStorageValue } from './use-session-storage';
import useSubscriptionsEnabled from './use-subscriptions-enabled';

const SESSION_STORAGE_KEY = 'PENDING-SUBSCRIPTION-PLAN';

/**
 * A Stripe Price lookup key, loosely. Deliberately a SHAPE check and not a lookup against
 * `resolve-tier.ts`'s map: lookup keys are ui-checkout's and Stripe's namespace, that app is the
 * authority on which ones exist, and it already shows an "isn't available" alert for one it cannot
 * resolve. Validating against our own list would reject a key that has legitimately been added
 * upstream but not mirrored here - a worse failure than passing it on. This only keeps junk out of
 * storage and off the frame URL.
 */
const LOOKUP_KEY_PATTERN = /^[a-z0-9_-]{1,64}$/i;

/**
 * Whether a string is shaped like a lookup key we are willing to carry. Exported so every writer
 * applies the same rule the reader does - a value stored by one path and refused by another is lost
 * silently, which is worse than refusing it up front.
 */
export function isPendingLookupKey(value: string | null | undefined): value is string {
  return !!value && LOOKUP_KEY_PATTERN.test(value);
}

const TIERS: readonly SubscriptionTier[] = ['plus', 'pro'];
const INTERVALS: readonly SubscriptionInterval[] = ['month', 'year'];

/**
 * A plan the customer chose before they could act on it - the same shape the checkout frame takes,
 * so a captured plan IS a target and needs no conversion between the two.
 *
 * `free` is deliberately not among the tiers. It is one in ui-checkout's own `TierId`, but a
 * customer cannot check out to Free, so it was never a valid target for the frame.
 */
export type PendingSubscriptionPlan = SubscriptionPlanTarget;

function isTier(value: string | null): value is SubscriptionTier {
  return !!value && TIERS.includes(value as SubscriptionTier);
}

function isInterval(value: string | null): value is SubscriptionInterval {
  return !!value && INTERVALS.includes(value as SubscriptionInterval);
}

/**
 * Resolves a plan from whatever it is given, or `null`. Used on BOTH legs - the URL at capture and
 * the stored value at read - so a plan can never reach the checkout frame without having satisfied
 * the same rules, whichever side it arrived from.
 */
function parsePendingPlan(source: { plan: string | null; tier: string | null; interval: string | null }) {
  const { plan, tier, interval } = source;

  // A valid pair beats a malformed lookup key. Preferring `plan` unconditionally would let
  // `?plan=junk&tier=plus&interval=month` throw away a perfectly good target for an unusable one.
  if (isPendingLookupKey(plan)) {
    return { plan } as PendingSubscriptionPlan;
  }
  if (isTier(tier) && isInterval(interval)) {
    return { tier, interval } as PendingSubscriptionPlan;
  }
  return null;
}

function parseFromParams(params: URLSearchParams): PendingSubscriptionPlan | null {
  return parsePendingPlan({
    plan: params.get('plan'),
    tier: params.get('tier'),
    interval: params.get('interval')
  });
}

/**
 * Re-checks a value read back out of storage. Storage is no more trustworthy than the URL that
 * filled it: a value written under an older shape, or by `setPendingSubscriptionPlan`, would
 * otherwise be cast straight to the type and flow onto the checkout frame's URL unexamined.
 */
function parseFromStorage(stored: unknown): PendingSubscriptionPlan | null {
  if (!stored || typeof stored !== 'object') {
    return null;
  }
  const { plan, tier, interval } = stored as Record<string, unknown>;
  return parsePendingPlan({
    plan: typeof plan === 'string' ? plan : null,
    tier: typeof tier === 'string' ? tier : null,
    interval: typeof interval === 'string' ? interval : null
  });
}

/**
 * Captures a pending subscription tier/interval (or `plan` lookup key) off the URL, once, and
 * mirrors it into sessionStorage before stripping it from the URL. Mount this once near the app
 * root (not per-route) - a customer following a tier-specific link may land on a different page
 * first (e.g. the dashboard) and only reach the page that consumes it (onboarding, My Plan) after an
 * in-app navigation the URL param itself would not survive.
 */
export function usePendingSubscriptionPlanCapture(): void {
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    const parsed = parseFromParams(searchParams);
    if (!parsed) {
      return;
    }
    setSessionStorageValue(SESSION_STORAGE_KEY, parsed);
    const next = new URLSearchParams(searchParams);
    next.delete('plan');
    next.delete('tier');
    next.delete('interval');
    setSearchParams(next, { replace: true });
    // Deliberately once-per-mount: re-running on every searchParams change would re-fire after our
    // own setSearchParams call above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}

/**
 * Reads the captured pending plan (see `usePendingSubscriptionPlanCapture`). Gated on the tenant's
 * `SUBSCRIPTIONS.ENABLED` setting - returns `null` for a tenant that doesn't sell subscriptions
 * regardless of what's in storage, so a stray/misconfigured link can never surface a checkout screen
 * anywhere it's read (onboarding, My Plan).
 */
export function usePendingSubscriptionPlan(): PendingSubscriptionPlan | null {
  const subscriptionsEnabled = useSubscriptionsEnabled();

  return useMemo(
    () => (subscriptionsEnabled ? parseFromStorage(getSessionStorageValue(SESSION_STORAGE_KEY)) : null),
    [subscriptionsEnabled]
  );
}

/**
 * Store a pending plan captured from somewhere other than the URL - specifically, before a
 * top-window sign-in redirect triggered by `subscription:auth-required`, so the customer comes back
 * to the plan they were choosing. Deliberately NOT gated on the tenant setting: the reader is, and
 * gating the write too would only hide a misconfiguration behind a second silent no-op.
 */
export function setPendingSubscriptionPlan(plan: PendingSubscriptionPlan): void {
  setSessionStorageValue(SESSION_STORAGE_KEY, plan);
}

/** Call once the pending plan has been acted on (subscribed, skipped, or handed off) so it can't
 * leak into a later, unrelated visit. */
export function clearPendingSubscriptionPlan(): void {
  setSessionStorageValue(SESSION_STORAGE_KEY, null);
}
